import { useState, useEffect } from "react";
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import Button from "../../components/Button";
import BreadCrumb from "../../components/BreadCrumb";
import useStyles from "./styles";
import { httprequest } from "../../data/api";
import { toast } from "react-toastify";
import PageLoader from "../../components/PageLoader";

const ReviewQuiz = () => {
  const { sessionId } = useParams();
  const classes = useStyles();
  const navigate = useNavigate();
  const [expandedAccordion, setExpandedAccordion] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResultData = async () => {
      try {
        setLoading(true);
        const resResult = await httprequest.get(`/api/v1/practice-sessions/${sessionId}/result`);
        if (resResult?.data?.data) {
          setResult(resResult.data.data);
        }
      } catch (err) {
        toast.error("Failed to load review data");
      } finally {
        setLoading(false);
      }
    };
    if (sessionId) fetchResultData();
  }, [sessionId]);

  const handleRetake = async () => {
    try {
      const res = await httprequest.post(`/api/v1/practice-sessions/${sessionId}/retake`);
      const newSessionId = res?.data?.data?.session?.id || res?.data?.data?.session_id;
      if (newSessionId) {
        navigate(`/student/my-learning/practice/${newSessionId}`);
      } else {
        toast.error("Could not determine new session ID.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to start retake session");
    }
  };

  const handleAccordionChange = (index) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? index : false);
  };

  if (loading) return <PageLoader />;

  const sessionInfo = result?.session || {};
  const stats = result?.summary || result?.metrics || result || {};
  const questionsList = result?.questions || result?.breakdown || [];

  // --- Pagination Logic ---
  const ITEMS_PER_PAGE = 5;
  const totalPages = Math.ceil(questionsList.length / ITEMS_PER_PAGE);
  const paginatedQuestions = questionsList.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  // -------------------------

  const totalAttempted = stats.total_attempted || stats.attempted || questionsList.filter(q => q.answered).length || 0;
  const totalCorrect = stats.total_correct || stats.correct || questionsList.filter(q => q.is_correct).length || 0;
  const totalIncorrect = stats.total_incorrect || stats.incorrect || questionsList.filter(q => !q.is_correct && q.user_answer).length || 0;
  const scoreBadge = stats.score_percentage != null ? `${stats.score_percentage}%` : stats.score != null ? `${stats.score}%` : "--";
  const timeSpent = stats.time_spent != null ? Math.ceil(stats.time_spent / 60) : "--";

  const getOptions = (questionObj) => {
    let rawOptions = typeof questionObj?.question === "object" ? questionObj?.question?.options : questionObj?.options;
    rawOptions = rawOptions || [];

    if (Array.isArray(rawOptions)) {
      return rawOptions.map((opt, i) => {
        if (typeof opt === "string") {
          return { id: String.fromCharCode(65 + i), text: opt };
        }

        return opt;
      });
    } else if (typeof rawOptions === "object") {
      return Object.entries(rawOptions).map(([key, value]) => {
        return { id: key, text: value === null ? "" : (typeof value === "object" ? value?.text || "" : value) };
      });
    }

    return [];
  };

  return (
    <Layout
      children={
        <div className="white_bg" style={{ minHeight: "100vh", padding: "20px" }}>
          {/* Top Bar with Breadcrumb and Retake Button */}
          <div className="breadcrumb-container" style={{ marginBottom: "30px" }}>
            <div>
              <BreadCrumb
                link_one="/student/my-learning"
                link_one_name="My Learning"
                active={`Review Answer - ${sessionInfo?.subject || "Quiz"}`}
                description={`${sessionInfo?.exam_type || "General"} ${sessionInfo?.year ? `- ${sessionInfo?.year}` : ""}`}
              />
            </div>
            <div style={{ width: "120px" }}>
              <Button
                variant="primary"
                buttonName="Retake"
                buttonSize="full"
                color="btndefault"
                onClick={handleRetake}
              />
            </div>
          </div>

          {/* Stats Cards */}
          <Grid container spacing={2} style={{ marginBottom: "30px" }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <div className={classes.statCard}>
                <p className={classes.statLabel}>Total Attempted Question</p>
                <h3 className={classes.statValueBlack}>{totalAttempted}</h3>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <div className={classes.statCard}>
                <p className={classes.statLabel}>Total Correct Answers</p>
                <h3 className={classes.statValueGreen}>{totalCorrect}</h3>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <div className={classes.statCard}>
                <p className={classes.statLabel}>Total Incorrect Answers</p>
                <h3 className={classes.statValueRed}>{totalIncorrect}</h3>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <div className={classes.statCard}>
                <p className={classes.statLabel}>Score Achievement</p>
                <h3 className={classes.statValueBlack}>{scoreBadge}</h3>
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <div className={classes.statCard}>
                <p className={classes.statLabel}>Total Time Spent</p>
                <h3 className={classes.statValueBlack}>{timeSpent} mins</h3>
              </div>
            </Grid>
          </Grid>

          {/* Questions Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h4 style={{ fontSize: "16px", fontWeight: "500", color: "#667085", margin: 0 }}>All Questions and Answers</h4>
            <button
              style={{
                display: "flex", alignItems: "center", gap: "8px", padding: "6px 12px",
                border: "1px solid #EBEBEB", borderRadius: "6px", backgroundColor: "#fff", cursor: "pointer",
                fontSize: "12px", color: "#667085"
              }}
            >
              Filter <Icon icon="jam:filter" width="14" />
            </button>
          </div>

          {/* Accordion List */}
          <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
            {paginatedQuestions.map((q, idx) => {
              const qOpts = getOptions(q);
              let qText = typeof q?.question === "object" ? q?.question?.text : q?.question;
              qText = qText || q?.question_text || q?.text || "";

              return (
                <div
                  key={q.id || idx}
                  style={{
                    border: "1px solid #EBEBEB",
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: "#fff"
                  }}
                >
                  <div style={{ padding: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center", marginBottom: "20px" }}>
                      <div style={{ color: "#8B92A5", fontSize: "12px", fontWeight: "500" }}>
                        Question {((currentPage - 1) * ITEMS_PER_PAGE) + idx + 1} of {questionsList.length}
                      </div>
                      <div>
                        {q.is_correct ? (
                          <Icon icon="lucide:check-circle-2" style={{ color: "#2EAC34", fontSize: "18px" }} />
                        ) : (
                          <Icon icon="radix-icons:cross-2" style={{ color: "#E93831", fontSize: "18px" }} />
                        )}
                      </div>
                    </div>

                    <div className={classes.questionText} style={{ marginBottom: "20px" }} dangerouslySetInnerHTML={{ __html: qText }} />

                    <div className={classes.optionsList} style={{ gap: "10px", marginBottom: "20px" }}>
                      {qOpts.map((opt) => (
                        <div key={opt.id} style={{ display: "flex", gap: "15px", alignItems: "center" }}>
                          <span style={{ fontSize: "14px", fontWeight: "400", width: "20px" }}>{opt.id}.</span>
                          <span style={{ fontSize: "14px" }} dangerouslySetInnerHTML={{ __html: opt.text }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  <Accordion
                    expanded={expandedAccordion === idx}
                    onChange={handleAccordionChange(idx)}
                    sx={{
                      boxShadow: "none",
                      borderTop: "1px solid #EBEBEB",
                      borderRadius: 0,
                      backgroundColor: "transparent",
                      "&:before": { display: "none" }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Icon
                          icon="carbon:chevron-right"
                          width="20"
                          style={{
                            transform: expandedAccordion === idx ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "all 0.3s"
                          }}
                        />
                      }
                      sx={{ padding: "0 20px" }}
                    >
                      <div style={{ fontSize: "14px", color: "#667085", fontWeight: "500", margin: 0 }}>
                        View Answer & Explanation
                      </div>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: "0 20px 20px 20px" }}>
                      <div
                        style={{
                          backgroundColor: "#F8F9FB", padding: "15px 20px", borderRadius: "8px",
                          display: "flex", flexDirection: "column", gap: "10px"
                        }}
                      >
                        <div style={{ fontSize: "14px" }}>
                          Correct Answer : <strong>{q.correct_answer || q.answer || "--"}</strong>
                        </div>
                        <div style={{ fontSize: "14px" }}>
                          Selected Answer: <strong style={{ color: q.is_correct ? "#2EAC34" : "#E93831" }}>{q.user_answer || "--"}</strong>
                        </div>
                        {q.explanation && (
                          <div style={{ fontSize: "14px", marginTop: "10px" }}>
                            <strong>Explanation:</strong> <span dangerouslySetInnerHTML={{ __html: q.explanation }} />
                          </div>
                        )}
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
              );
            })}
          </div>

          {/* Dynamic Pagination Footer */}
          {totalPages > 1 && (
            <div className={classes.quizPaginationWrapper} style={{ marginTop: "40px" }}>
              <button
                className={`${classes.quizPageBtn} ${classes.quizPageBtnNumber}`}
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                style={{ opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
              >
                <Icon icon="carbon:chevron-left" width="16" />
              </button>

              <div className={classes.quizPageNumbers}>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(num => (
                  <button
                    key={num}
                    onClick={() => handlePageClick(num)}
                    className={`${classes.quizPageBtn} ${classes.quizPageBtnNumber} ${num === currentPage ? classes.quizPageNumberCurrent : ""}`}
                  >
                    {num}
                  </button>
                ))}
              </div>

              <button
                className={`${classes.quizPageBtn} ${classes.quizPageBtnNumber}`}
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                style={{ opacity: currentPage === totalPages ? 0.5 : 1, cursor: currentPage === totalPages ? "not-allowed" : "pointer" }}
              >
                <Icon icon="carbon:chevron-right" width="16" />
              </button>
            </div>
          )}
        </div>
      }
    />
  );
};

export default ReviewQuiz;
